/**
 * Рамка экрана: переходы «толчком» и жест «назад» от края, как в iOS и Telegram.
 *
 * Роутер снимает уходящий экран и ставит новый в один момент, поэтому обе
 * стороны перехода одновременно в дереве не бывают. Чтобы новый экран въезжал
 * ПОВЕРХ старого, а при возврате старый уезжал, открывая предыдущий, рамка
 * пользуется снимками — копией DOM экрана, снятой в момент ухода с него
 * (app/model/screen-snapshots.ts). Снимок неживой и живёт ровно столько,
 * сколько идёт анимация.
 *
 *  - вперёд: экран въезжает справа, снимок предыдущего под ним отъезжает
 *    на треть и темнеет;
 *  - назад по кнопке: экран уезжает вправо над снимком предыдущего, и только
 *    потом происходит переход — новый экран встаёт на место снимка без
 *    повторной анимации;
 *  - назад системным жестом или кнопкой браузера: переход уже произошёл,
 *    поэтому над новым экраном уезжает снимок того, с которого вернулись;
 *  - жест: касание у левого края (32 px) тянет экран за пальцем; отпускание
 *    дальше трети ширины или быстрое движение завершает возврат, иначе экран
 *    возвращается на место.
 *
 * Всё это — только в одноколоночной раскладке (mobile/tablet) и не при
 * `prefers-reduced-motion`; на desktop вложенные экраны мягко проявляются.
 * На Android с жестовой навигацией край экрана забирает система, и «назад»
 * там делает она же; в установленном iPhone Web App своего жеста нет —
 * этот единственный. Во вкладке Safari на iPhone жест от края и анимация
 * возврата принадлежат браузеру (`browserOwnsBackGesture`): свой жест там
 * выключен, а вход после перехода по истории не анимируется — иначе экран
 * уезжал бы дважды.
 *
 * Анимация входа стартует кадром позже монтирования: тяжёлый первый рендер
 * (список, лента) иначе съедал начало анимации, и экран «прыгал» на середину
 * пути. До старта экран стоит там, откуда поедет, а снимок — на своём месте.
 * Класс анимации и снятие стартового положения применяются одним заданием
 * (`flushSync`): иначе браузер успевал отрисовать экран на месте, и он
 * вспыхивал, а потом уезжал к старту.
 *
 * Слой снимка живёт внутри области перехода (`[data-nav-area]` — в оболочке
 * это экран вместе с полосами над ним; у публичных страниц — родитель рамки)
 * и растянут на неё абсолютно: баннеры, которых нет на вложенном экране,
 * уезжают и приезжают вместе со снимком, а когда клавиатура iOS сжимает и
 * сдвигает корень приложения, слой сжимается вместе с ним. Слой с фиксированным
 * прямоугольником, снятым при монтировании, переставал совпадать с рамкой, и
 * из-под неё выглядывал снимок (синие вспышки на iPhone при входе). Пока слой
 * есть, рамка — отдельный контекст наложения: шапка нового экрана (`z-index`
 * sticky) иначе рисовалась поверх уезжающего снимка.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { createPortal, flushSync } from 'react-dom'

import { hasOpenLayers } from '@shared/lib/modal-stack'
import { VelocityTracker, clamp, haptic, isTouchLike, reducedMotion } from '@shared/lib/motion'
import { MEDIA, useMediaQuery } from '@shared/lib/use-media-query'
import { browserOwnsBackGesture } from '@shared/pwa/platform'
import { cx } from '@shared/ui'

import {
  getCurrentKey,
  getNavigationDirection,
  getNavigationFromKey,
  getPreviousKey,
  markSkipNextEnter,
  overrideNextDirection,
  shouldSkipEnter,
} from '../model/navigation-direction'
import { restoreScreenScroll } from '../model/screen-scroll'
import {
  NAV_AREA_ATTR,
  NAV_LAYER_ATTR,
  NAV_NESTED_ATTR,
  NAV_SCREEN_ATTR,
  takeScreenSnapshot,
  type SnapshotHandle,
} from '../model/screen-snapshots'
import styles from './ScreenFrame.module.css'

/** Полоса у левого края, с которой начинается жест. */
const EDGE_PX = 32
/** Смещение, после которого касание считается жестом, а не нажатием. */
const ACTIVATE_PX = 8
/** Дальше этой доли ширины отпускание завершает возврат. */
const COMMIT_RATIO = 0.35
/** Быстрый взмах завершает возврат с любого места (px/мс). */
const COMMIT_VELOCITY = 0.4
/** Движение обратно отменяет возврат даже за порогом. */
const CANCEL_VELOCITY = -0.3
/** Насколько нижний экран отъезжает под верхним (доля ширины), как в iOS. */
const PARALLAX = 0.3
const DIM_MAX = 0.22
/** Доводка после отпускания. */
const SETTLE_MS = 260
/** Запасной таймер на случай, если `animationend` не пришёл. */
const ANIMATION_FALLBACK_MS = 700

export interface ScreenFrameHandle {
  /**
   * Уйти «назад»: если есть снимок предыдущего экрана, сначала проиграть уход,
   * затем выполнить переход; иначе перейти сразу, подсказав направление.
   */
  pop: (navigate: () => void) => void
}

interface ScreenFrameProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Вложенный экран: анимируется вход по направлению перехода, доступен жест. */
  nested?: boolean
  /** Переход «назад» для жеста от края; без него жеста нет. */
  onBack?: () => void
  children: ReactNode
}

type Motion = 'push' | 'reveal' | 'pop' | 'drag' | 'enterForward' | 'enterBack'

interface Layer {
  /** Под экраном (предыдущий) или поверх (тот, с которого вернулись). */
  kind: 'under' | 'over'
  snapshot: SnapshotHandle | null
  /** Область перехода — слой растягивается на неё и живёт в ней порталом. */
  host: HTMLElement
  motion: 'push' | 'pop' | 'reveal' | 'drag'
}

interface Gesture {
  pointerId: number
  startX: number
  startY: number
  width: number
  active: boolean
  progress: number
  hapticDone: boolean
  frame: number
  tracker: VelocityTracker
  cleanup: () => void
}

/**
 * Область, в которой живёт слой снимка: колонка оболочки с полосами над экраном
 * или, у публичных страниц, родитель рамки (`#root`). Оба позиционированы —
 * слой растягивается на них `inset: 0` и следует за любой сменой раскладки.
 */
function hostOf(frame: HTMLElement): HTMLElement {
  return frame.closest<HTMLElement>(`[${NAV_AREA_ATTR}]`) ?? frame.parentElement ?? frame
}

export const ScreenFrame = forwardRef<ScreenFrameHandle, ScreenFrameProps>(function ScreenFrame(
  { nested = false, onBack, className, children, ...rest },
  ref,
) {
  const frameRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useMediaQuery(MEDIA.fromDesktop)

  const [motion, setMotion] = useState<Motion | null>(null)
  const [layer, setLayer] = useState<Layer | null>(null)

  const busyRef = useRef(false)
  const motionRef = useRef<Motion | null>(null)
  motionRef.current = motion
  /** Отложенный старт входа: до него экран стоит на стартовой позиции. */
  const enterFrameRef = useRef(0)
  const gestureRef = useRef<Gesture | null>(null)
  const afterPopRef = useRef<(() => void) | null>(null)
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  const clearFallback = useCallback((): void => {
    if (fallbackRef.current) clearTimeout(fallbackRef.current)
    fallbackRef.current = null
  }, [])

  const settle = useCallback((): void => {
    clearFallback()
    // Вход мог не стартовать (кадры не приходят в скрытой вкладке) — экран не должен остаться за краем
    cancelAnimationFrame(enterFrameRef.current)
    enterFrameRef.current = 0
    if (frameRef.current) frameRef.current.style.transform = ''
    busyRef.current = false
    setMotion(null)
    setLayer(null)
  }, [clearFallback])

  /** Анимация без `animationend` (стили не применились) не должна вешать рамку. */
  const armFallback = useCallback(
    (finish: () => void): void => {
      clearFallback()
      fallbackRef.current = setTimeout(finish, ANIMATION_FALLBACK_MS)
    },
    [clearFallback],
  )

  /* --- Прокрутка экрана, с которого уходили ------------------------------ */

  /*
   * До анимации входа: экран этой записи истории уже был прокручен, и возврат
   * на него не должен показывать список сверху (app/model/screen-scroll.ts).
   */
  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    // Только при монтировании: позиция относится к записи, которая показала экран.
    return restoreScreenScroll(getCurrentKey(), frame)
  }, [])

  /* --- Вход при монтировании ------------------------------------------- */

  useLayoutEffect(() => {
    if (shouldSkipEnter()) return

    const direction = getNavigationDirection()
    if (direction === 'none') return
    // Вперёд анимируются только вложенные экраны: переключение разделов — без движения.
    if (direction === 'forward' && !nested) return

    const frame = frameRef.current
    if (!frame || isDesktop || reducedMotion()) {
      if (nested) setMotion(direction === 'forward' ? 'enterForward' : 'enterBack')
      return
    }

    const snapshot = takeScreenSnapshot(getNavigationFromKey())
    const host = hostOf(frame)

    let start: Motion
    if (direction === 'forward') {
      setLayer({ kind: 'under', snapshot, host, motion: 'push' })
      // До старта экран стоит за правым краем — над снимком предыдущего
      frame.style.transform = 'translateX(100%)'
      start = 'push'
    } else if (snapshot && (nested || snapshot.nested)) {
      // Назад: поверх уезжает снимок экрана, с которого вернулись, — и на корневой раздел тоже,
      // если ушли с вложенного (системная «Назад» из диалога в список).
      setLayer({ kind: 'over', snapshot, host, motion: 'reveal' })
      start = 'reveal'
    } else if (nested) {
      setMotion('enterBack')
      return
    } else {
      return
    }

    busyRef.current = true
    armFallback(settle)

    /*
     * Два кадра: первый — браузер отрисовал смонтированный экран (самая дорогая часть),
     * второй — стартует анимация. Иначе она начиналась бы во время первой отрисовки и
     * пропускала начало.
     */
    enterFrameRef.current = requestAnimationFrame(() => {
      enterFrameRef.current = requestAnimationFrame(() => {
        enterFrameRef.current = 0
        // Класс анимации — синхронно, до снятия стартового положения: между ними не должно быть кадра
        flushSync(() => setMotion(start))
        frame.style.transform = ''
      })
    })
    return () => {
      cancelAnimationFrame(enterFrameRef.current)
      enterFrameRef.current = 0
      // Повторный запуск эффекта (StrictMode) должен мерить рамку на месте, а не за краем
      frame.style.transform = ''
    }
    // Только при монтировании: направление и снимок относятся к переходу, который показал экран.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => clearFallback(), [clearFallback])

  const onAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return

    if (motion === 'pop') {
      clearFallback()
      const finish = afterPopRef.current
      afterPopRef.current = null
      finish?.()
      // Если переход не снял рамку (маршрут остался), вернуть её в строй.
      fallbackRef.current = setTimeout(settle, 300)
      return
    }

    if (motion === 'push' || motion === 'reveal' || motion === 'enterForward' || motion === 'enterBack') {
      settle()
    }
  }

  /* --- Уход «назад» по кнопке -------------------------------------------- */

  const pop = useCallback(
    (navigate: () => void): void => {
      const frame = frameRef.current

      if (busyRef.current) {
        // Уход уже идёт (кнопка или жест) — второе нажатие ничего не меняет
        if (motionRef.current === 'pop' || motionRef.current === 'drag') return
        /*
         * Вход ещё не доиграл, а человек уже нажал «назад»: прерываем вход —
         * экран встаёт на место, и уход начинается сразу. Иначе быстрое нажатие
         * терялось (проверка E2E ловила это под нагрузкой).
         */
        cancelAnimationFrame(enterFrameRef.current)
        enterFrameRef.current = 0
        if (frame) frame.style.transform = ''
        settle()
      }

      if (!frame || !nested || isDesktop || reducedMotion()) {
        navigate()
        return
      }

      const snapshot = takeScreenSnapshot(getPreviousKey())
      if (!snapshot) {
        // Снимка нет (deep link): переход сразу, но входящий экран покажет уход правильно.
        overrideNextDirection('back')
        navigate()
        return
      }

      busyRef.current = true
      afterPopRef.current = () => {
        markSkipNextEnter()
        navigate()
      }
      setLayer({ kind: 'under', snapshot, host: hostOf(frame), motion: 'pop' })
      setMotion('pop')
      armFallback(() => {
        const finish = afterPopRef.current
        afterPopRef.current = null
        finish?.()
        settle()
      })
    },
    [armFallback, isDesktop, nested, settle],
  )

  useImperativeHandle(ref, () => ({ pop }), [pop])

  /* --- Жест от левого края -------------------------------------------------- */

  const applyDrag = (progress: number): void => {
    const frame = frameRef.current
    if (frame) frame.style.transform = `translateX(${progress * 100}%)`
    const snapshot = layerRef.current?.querySelector<HTMLElement>('[data-snapshot]')
    const dim = layerRef.current?.querySelector<HTMLElement>('[data-dim]')
    if (snapshot) snapshot.style.transform = `translateX(${-PARALLAX * 100 * (1 - progress)}%)`
    if (dim) dim.style.opacity = String(DIM_MAX * (1 - progress))
  }

  const finishGesture = (commit: boolean): void => {
    const gesture = gestureRef.current
    const frame = frameRef.current
    gestureRef.current = null
    if (!gesture || !frame) return
    cancelAnimationFrame(gesture.frame)
    gesture.cleanup()

    const transition = `transform ${SETTLE_MS}ms var(--ease-ios)`
    frame.style.transition = transition
    const snapshot = layerRef.current?.querySelector<HTMLElement>('[data-snapshot]')
    const dim = layerRef.current?.querySelector<HTMLElement>('[data-dim]')
    if (snapshot) snapshot.style.transition = transition
    if (dim) dim.style.transition = `opacity ${SETTLE_MS}ms var(--ease-ios)`

    // Кадр спустя — чтобы переход начался от текущего положения, а не «перепрыгнул».
    requestAnimationFrame(() => applyDrag(commit ? 1 : 0))

    const done = (): void => {
      if (commit) {
        markSkipNextEnter()
        onBackRef.current?.()
        // Переход обычно снимает рамку; если нет — вернуть её на место.
        fallbackRef.current = setTimeout(() => {
          frame.style.transition = ''
          frame.style.transform = ''
          settle()
        }, 300)
        return
      }
      frame.style.transition = ''
      frame.style.transform = ''
      settle()
    }

    let finished = false
    const onEnd = (event: TransitionEvent): void => {
      if (event.target !== frame || finished) return
      finished = true
      frame.removeEventListener('transitionend', onEnd)
      clearFallback()
      done()
    }
    frame.addEventListener('transitionend', onEnd)
    armFallback(() => {
      if (finished) return
      finished = true
      frame.removeEventListener('transitionend', onEnd)
      done()
    })
  }

  const onPointerDownCapture = (event: React.PointerEvent<HTMLDivElement>): void => {
    const frame = frameRef.current
    if (!frame || !nested || !onBackRef.current || isDesktop || busyRef.current) return
    if (!isTouchLike(event) || !event.isPrimary || reducedMotion() || hasOpenLayers()) return
    // Во вкладке Safari на iPhone край экрана — жест браузера; свой мешал бы ему
    if (browserOwnsBackGesture()) return

    const rect = frame.getBoundingClientRect()
    if (event.clientX - rect.left > EDGE_PX) return

    // Касание у края — наше: строки, пузыри и карточки под ним свои жесты не начинают.
    event.stopPropagation()
    frame.setPointerCapture(event.pointerId)

    const tracker = new VelocityTracker()
    tracker.push(event.clientX, event.clientY)

    const onMove = (native: PointerEvent): void => {
      const gesture = gestureRef.current
      if (!gesture || native.pointerId !== gesture.pointerId) return

      const dx = native.clientX - gesture.startX
      const dy = native.clientY - gesture.startY
      gesture.tracker.push(native.clientX, native.clientY)

      if (!gesture.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          // Вертикальное движение — это прокрутка, отдаём её браузеру.
          abortGesture()
          return
        }
        if (dx < ACTIVATE_PX) return

        gesture.active = true
        busyRef.current = true
        setLayer({
          kind: 'under',
          snapshot: takeScreenSnapshot(getPreviousKey()),
          host: hostOf(frame),
          motion: 'drag',
        })
        setMotion('drag')
      }

      gesture.progress = clamp(dx / gesture.width, 0, 1)
      if (!gesture.hapticDone && gesture.progress >= COMMIT_RATIO) {
        gesture.hapticDone = true
        haptic('light')
      }

      cancelAnimationFrame(gesture.frame)
      gesture.frame = requestAnimationFrame(() => applyDrag(gesture.progress))
    }

    const onUp = (native: PointerEvent): void => {
      const gesture = gestureRef.current
      if (!gesture || native.pointerId !== gesture.pointerId) return
      if (!gesture.active) {
        abortGesture()
        return
      }
      const velocity = gesture.tracker.velocity().x
      const commit =
        velocity > COMMIT_VELOCITY || (gesture.progress > COMMIT_RATIO && velocity > CANCEL_VELOCITY)
      finishGesture(commit)
    }

    const onCancel = (): void => {
      const gesture = gestureRef.current
      if (!gesture) return
      if (gesture.active) finishGesture(false)
      else abortGesture()
    }

    const abortGesture = (): void => {
      const gesture = gestureRef.current
      gestureRef.current = null
      gesture?.cleanup()
    }

    frame.addEventListener('pointermove', onMove)
    frame.addEventListener('pointerup', onUp)
    frame.addEventListener('pointercancel', onCancel)

    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      active: false,
      progress: 0,
      hapticDone: false,
      frame: 0,
      tracker,
      cleanup: () => {
        frame.removeEventListener('pointermove', onMove)
        frame.removeEventListener('pointerup', onUp)
        frame.removeEventListener('pointercancel', onCancel)
        try {
          frame.releasePointerCapture(event.pointerId)
        } catch {
          /* указатель уже отпущен */
        }
      },
    }
  }

  /*
   * Пока есть слой снимка (в том числе до старта анимации) — рамка отдельный контекст
   * наложения и готовит слой заранее (`will-change`): снимок поверх должен закрывать и шапку.
   */
  const moving = layer !== null

  // Слой — порталом в область перехода: он растянут на неё и не зависит от места рамки в DOM
  const snapshotLayer = layer
    ? createPortal(<SnapshotLayer layer={layer} armed={motion !== null} elementRef={layerRef} />, layer.host)
    : null

  return (
    <>
      {snapshotLayer}

      <div
        ref={frameRef}
        {...rest}
        {...{ [NAV_SCREEN_ATTR]: true, [NAV_NESTED_ATTR]: nested || undefined }}
        className={cx(
          styles['frame'],
          motion === 'push' && styles['pushIn'],
          motion === 'reveal' && styles['popReveal'],
          motion === 'pop' && styles['popOut'],
          motion === 'enterForward' && styles['enterForward'],
          motion === 'enterBack' && styles['enterBack'],
          className,
        )}
        data-moving={moving || undefined}
        onPointerDownCapture={onPointerDownCapture}
        onAnimationEnd={onAnimationEnd}
      >
        {children}
      </div>
    </>
  )
})

/* ===================================================================== */
/* Слой со снимком                                                         */
/* ===================================================================== */

function SnapshotLayer({
  layer,
  armed,
  elementRef,
}: {
  layer: Layer
  /** Анимация слоя стартует вместе с анимацией экрана, а не при монтировании. */
  armed: boolean
  elementRef: React.RefObject<HTMLDivElement>
}): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || !layer.snapshot) return
    host.replaceChildren(layer.snapshot.node)
    layer.snapshot.hydrate()
    return () => host.replaceChildren()
  }, [layer])

  return (
    <div
      ref={elementRef}
      className={cx(
        styles['layer'],
        layer.kind === 'under' ? styles['layerUnder'] : styles['layerOver'],
        armed && layer.motion === 'push' && styles['layerPush'],
        armed && layer.motion === 'pop' && styles['layerPop'],
        armed && layer.motion === 'reveal' && styles['layerReveal'],
        layer.motion === 'drag' && styles['layerDrag'],
      )}
      aria-hidden
      {...{ [NAV_LAYER_ATTR]: true }}
    >
      {layer.kind === 'over' ? <div className={styles['dim']} data-dim /> : null}
      <div
        ref={hostRef}
        className={cx(styles['snapshot'], layer.kind === 'over' && styles['snapshotOver'])}
        data-snapshot
      />
      {layer.kind === 'under' ? <div className={styles['dim']} data-dim /> : null}
    </div>
  )
}
